import { FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAuthenticatedUser,
  registerNewUser,
} from "../../features/auth-module/authAction";

import CompanyImage from "../../images/png/LoginPageImage.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5),
  background: "#fff",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    minHeight: "55vh",
    order: 2,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    justifyContent: "flex-start",
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundSize: "cover",
  backgroundPosition: "center",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  position: "relative",
  borderRadius: 10,
  transform: "scale(1)",
  [theme.breakpoints.down("md")]: {
    minHeight: "35vh",
    order: 1,
    backgroundPosition: "top center",
    backgroundSize: "120%",
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  marginTop: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8),
    maxWidth: "100%",
    marginTop: theme.spacing(1.5),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "28px",
  fontWeight: 700,
  color: "#455354",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#6E6E6E",
  fontSize: "12px",
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    marginBottom: theme.spacing(1.5),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: "6px",
  padding: theme.spacing(1),
  fontSize: "12px",
  "&:hover": {
    background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    minHeight: "37px",
  },
}));

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    password: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    emailId: false,
    password: false,
    confirmPassword: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, user } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (error) toast.error(error);
    if (user && !loading) {
      navigate("/subscription-plan");
    }
  }, [error, user, loading, navigate]);

  const validateEmail = (value: string) => {
    if (!value) return "Email Address is required";
    if (value.includes(" ")) return "Email Address cannot contain spaces";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\\s@]+$/;
    if (!emailRegex.test(value.trim()))
      return "Enter a valid email address (e.g., user@domain.com)";
    return "";
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "firstName":
        if (!value) return "First name is required";
        if (value.includes(" ")) return "First name cannot contain spaces";
        return "";
      case "lastName":
        if (!value) return "Last name is required";
        if (value.includes(" ")) return "Last name cannot contain spaces";
        return "";
      case "emailId":
        return validateEmail(value);
      case "password":
        if (!value) return "Password is required";
        if (value.includes(" ")) return "Password cannot contain spaces";
        if (value.length < 8) return "Password must be at least 8 characters";
        return "";
      case "confirmPassword":
        if (!value) return "Please confirm your password";
        if (value.includes(" "))
          return "Confirm Password cannot contain spaces";
        if (value !== formData.password) return "Passwords do not match";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (value.includes(" ")) {
      toast.error(
        `${
          name === "firstName"
            ? "First Name"
            : name === "lastName"
            ? "Last Name"
            : name === "emailId"
            ? "Email Address"
            : name === "password"
            ? "Password"
            : "Confirm Password"
        } cannot contain spaces`
      );
      return;
    }

    const trimmedValue = value.trim();
    setFormData((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));

    if (touched[name as keyof typeof touched]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, trimmedValue),
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(
        name,
        formData[name as keyof typeof formData].trim()
      ),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors = {
      firstName: validateField("firstName", formData.firstName),
      lastName: validateField("lastName", formData.lastName),
      emailId: validateEmail(formData.emailId),
      password: validateField("password", formData.password),
      confirmPassword: validateField(
        "confirmPassword",
        formData.confirmPassword
      ),
    };

    setErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      emailId: true,
      password: true,
      confirmPassword: true,
    });

    const hasError = Object.values(newErrors).some((msg) => msg);
    if (!hasError) {
      const registerResult = await dispatch(
        registerNewUser({
          firstname: formData.firstName,
          lastname: formData.lastName,
          email: formData.emailId,
          password: formData.password,
        })
      ).unwrap();

      if (registerResult.success) {
        const profileResult = await dispatch(getAuthenticatedUser()).unwrap();

        if (profileResult.success) {
          const profileUser = profileResult.data?.user;
          if (profileUser?.subscription?.id) {
            navigate("/main/dashboard");
          } else {
            navigate("/subscription-plan");
          }
        } else {
          console.error("Failed to fetch user profile:", profileResult.message);
          setErrors((prev) => ({
            ...prev,
            emailId: profileResult.message || "Failed to fetch user details",
          }));
        }
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      agreed && isFormValid && handleSubmit(e as any);
    }
  };

  const isFormValid =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.emailId.trim() !== "" &&
    formData.password.trim() !== "" &&
    formData.confirmPassword.trim() !== "" &&
    agreed;

  const backgroundImageUrl = CompanyImage;

  return (
    <Container>
      <ImageSection sx={{ backgroundImage: `url(${backgroundImageUrl})` }} />
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={(e: FormEvent) => handleSubmit(e)}
            onKeyDown={(e: KeyboardEvent) => handleKeyPress(e)}
          >
            <WelcomeText>Create Account</WelcomeText>
            <Subtitle>Fill details to create account</Subtitle>

            <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FieldLabel>First Name</FieldLabel>
                <StyledTextField
                  autoFocus
                  name="firstName"
                  variant="outlined"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FieldLabel>Last Name</FieldLabel>
                <StyledTextField
                  name="lastName"
                  variant="outlined"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 0 }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FieldLabel>Email Address</FieldLabel>
                <StyledTextField
                  name="emailId"
                  variant="outlined"
                  type="text"
                  autoComplete="email"
                  value={formData.emailId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.emailId}
                  helperText={errors.emailId}
                  required
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 0 }}
                  inputProps={{ pattern: "[^\\s@]+@[^\\s@]+\\.[^\\s@]+" }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FieldLabel>Password</FieldLabel>
                <StyledTextField
                  name="password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size={isMobile ? "small" : "medium"}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FieldLabel>Confirm Password</FieldLabel>
                <StyledTextField
                  name="confirmPassword"
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  required
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: -1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showConfirmPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            size={isMobile ? "small" : "medium"}
                          >
                            {showConfirmPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size={"small"}
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      I agree with this{" "}
                      <Typography
                        variant="body2"
                        component={RouterLink}
                        to="/terms-conditions"
                        sx={{
                          textDecoration: "none",
                          color: "#455354",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Terms & Privacy
                      </Typography>
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            <motion.div
              whileHover={{
                scale: 1.01,
                boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
              }}
              whileTap={{ scale: 0.97 }}
              style={{ width: "100%" }}
            >
              <PrimaryButton
                type="submit"
                variant="contained"
                disabled={loading || !isFormValid}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Signing up..." : "Sign up"}
              </PrimaryButton>
            </motion.div>

            <Box
              sx={{
                width: "100%",
                mt: 1.5,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontFamily: "'Almarai', sans-serif",
                  color: "#455354",
                }}
              >
                Already have an account?
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/login"
                color="primary"
                sx={{
                  textDecoration: "none",
                  ml: 0.5,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  fontFamily: "'Almarai', sans-serif",
                  color: "#455354",
                }}
              >
                Log in
              </Typography>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default Register;
